import type { i18n as I18n } from 'i18next';
import moment from 'moment-timezone';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import Moment from 'react-moment';
import { el, enUS, es, fr, it, pl, pt } from 'date-fns/locale';

// moment ships `en` in the core bundle; every other language needs its locale pulled in.
// The locale bundles live in the `moment` package that `moment-timezone` depends on and
// extend the very same moment instance; `moment-timezone` has no entry point of its own
// for them, so importing them directly is the only way in.
/* eslint-disable import/no-extraneous-dependencies */
import 'moment/locale/el';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/pl';
import 'moment/locale/pt';
/* eslint-enable import/no-extraneous-dependencies */

// date-fns drives react-datepicker: the calendar's month/weekday names, its first day
// of the week, and the localized `P`/`p` tokens the pickers format their inputs with.
const DATE_FNS_LOCALES = { en: enUS, el, es, fr, it, pl, pt };

type SupportedLanguage = keyof typeof DATE_FNS_LOCALES;

const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

const isSupported = (language: string): language is SupportedLanguage => language in DATE_FNS_LOCALES;

// i18next hands back tags like `en-GB`; the locale bundles are keyed by the base language.
const resolveLanguage = (language?: string): SupportedLanguage => {
  const base = (language ?? '').split('-')[0];

  return isSupported(base) ? base : FALLBACK_LANGUAGE;
};

Object.entries(DATE_FNS_LOCALES).forEach(([language, locale]) => registerLocale(language, locale));

/** Points moment, react-moment and react-datepicker at the locale for `language`. */
export const applyDateTimeLocale = (language?: string): void => {
  const resolved = resolveLanguage(language);

  moment.locale(resolved);
  // react-moment reads this at render time, so <Moment> follows moment itself.
  Moment.globalLocale = resolved;
  setDefaultLocale(resolved);
};

/**
 * Keeps the date/time libraries on the language i18next is serving.
 *
 * Takes the instance as an argument rather than importing it so this module stays
 * free of a cycle with `./index`, which is what calls it.
 */
export const bindDateTimeLocale = (i18n: I18n): void => {
  applyDateTimeLocale(i18n.language);
  i18n.on('languageChanged', applyDateTimeLocale);
};
